import Pagination from "../../../../../components/common/Table/Pagination";
import React from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumbs from "../../../../../components/common/BreadCrumbs/BreadCrumbs";
import uploadOutline from "../../../../../../public/assets/images/uploadOutline.svg";
import UploadErrorBanner from "../../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import Table from "../../../../../components/common/Table/Table";
import { flattenApplicationData } from "../../../../../utils/flattenApplicationData";
import { ERROR_FIELD_MAP } from "../../../../../utils/errorFieldMap";
import pencilIcon from "../../../../../../public/assets/images/pencil-outline.svg";
import trashSolid from "../../../../../../public/assets/images/trash-solid.svg";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import {
  uploadExcelFile,
  getApplicationErrors,
  deleteOneRecord,
  deleteAllRecords,
} from "../../../../../store/slices/Tuition/administerSlice";
import Loader from "../../../../../components/common/Loader";
import { fetchDropdownOptions } from "../../../../../store/slices/Tuition/tuitionApplySlice";
import { formatTimestampMDY } from "../../../../../utils/date";

interface ApiFieldError {
  fieldName: string;
  errorMessage: string;
}

// Type for field error with step (after mapping)
interface FieldError extends ApiFieldError {
  step: number;
}

// Type for application with errors
interface ApplicationWithErrors {
  applicationId: string;
  employeeName: string;
  employeeId: string;
  submittedDate?: string;
  errors: FieldError[]; // Array of all errors for this application
}
function processApplicationsWithErrors(data: any): any[] {
  return (data.data || []).map((app: any) => {
    // Parse errors if stringified JSON
    let errorObj: Record<string, string[]> = {};
    if (typeof app.errors === "string") {
      try {
        errorObj = JSON.parse(app.errors);
      } catch {
        // fallback: treat as global error
        errorObj = { api: [app.errors] };
      }
    } else if (typeof app.errors === "object" && app.errors !== null) {
      errorObj = app.errors;
    }
    // Map backend error keys to frontend fields using ERROR_FIELD_MAP
    const mappedErrors = Object.entries(errorObj)
      .map(([backendKey, messages]) => {
        const frontendField = ERROR_FIELD_MAP[backendKey];
        if (frontendField) {
          return {
            fieldName: frontendField,
            errorMessage: Array.isArray(messages) ? messages[0] : messages,
            step: 4, // Assuming step 4 for tuition fields
            backendKey, // <-- preserve backend key for PATCH payload
          };
        } else {
          return {
            fieldName: backendKey, // fallback to backend key if not mapped
            errorMessage: Array.isArray(messages) ? messages[0] : messages,
            step: 1,
            backendKey,
          };
        }
      })
      .filter((e) => e.fieldName); // Only include mapped errors
    return {
      ...app,
      errors: mappedErrors,
    };
  });
}
// Helper to determine the earliest step with an error
export function getEarliestStep(errors: any): number {
  let earliestStep = 5;
  if (errors) {
    let errorObj: Record<string, any> = {};
    if (typeof errors === "string") {
      try {
        errorObj = JSON.parse(errors);
      } catch {
        errorObj = {};
      }
    } else if (typeof errors === "object" && errors !== null) {
      errorObj = errors;
    }
    // If BALANCE_ERROR is present, go to step 5
    if (Object.keys(errorObj).includes("BALANCE_ERROR")) {
      return 5;
    }
    // Map backend error keys to steps (customize as needed)
    const errorKeyToStep: Record<string, number> = {
      SCHOOL_NAME_UNDER_CLASS_INFO: 5,
      DUPLICATE_RECORD: 1,
      // Add more mappings as needed
    };
    const errorSteps = Object.keys(errorObj)
      .map((key) => errorKeyToStep[key])
      .filter((step) => typeof step === "number");
    if (errorSteps.length > 0) {
      earliestStep = Math.min(...errorSteps);
    }
  }
  return earliestStep;
}
const AdministerSystem: React.FC = () => {
  const uploadError = useAppSelector((state) => state.administer.uploadError);
  const deleteAllError = useAppSelector(
    (state) => state.administer.deleteAllError,
  );
  // State to control visibility of the template upload banner
  const [showUploadBanner, setShowUploadBanner] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const uploadStatus = useAppSelector((state) => state.administer.uploadStatus);
  const deleteStatus = useAppSelector((state) => state.administer.deleteStatus);
  const errorRecordsStatus = useAppSelector(
    (state) => state.administer.errorRecordsStatus,
  );
  const deleteAllStatus = useAppSelector(
    (state) => state.administer.deleteAllStatus,
  );
  const submitCorrectedStatus = useAppSelector(
    (state) => state.administer.submitCorrectedStatus,
  );

  // Show loader if any API is loading
  const isLoading = [
    uploadStatus,
    deleteStatus,
    errorRecordsStatus,
    deleteAllStatus,
    submitCorrectedStatus,
  ].includes("loading");

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = React.useState<string>("");
  const [fileSizeError, setFileSizeError] = React.useState<string>("");
  const [fileTypeError, setFileTypeError] = React.useState<string>("");
  const MAX_FILE_SIZE_MB = 9;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const perPage = 5;
  const [totalItems, setTotalItems] = React.useState(0);

  // Process API response to add step numbers
  const [applicationsWithErrors, setApplicationsWithErrors] = React.useState<
    ApplicationWithErrors[]
  >([]);
  // Navigate to error correction page with all errors for the clicked application
  const dropdownOptions = useAppSelector(
    (state) => state.tuitionApply.dropdownOptions,
  );
    // Fetch dropdown options for all dropdown fields used in the form
    React.useEffect(() => {
      if (Object.keys(dropdownOptions).length === 0) {
        dispatch(
          fetchDropdownOptions({ fieldName: "all", applType: "AT001" }) as any,
        );
      }
    }, [dispatch, dropdownOptions]);
  // Helper to lookup dropdown value by field and option key (module scope)
  const getDropdownValue = (
    options: any[] | undefined,
    codeValue: string | number | undefined,
  ): string | undefined => {
    if (!options) return undefined;
    const found = options.find(
      (item: any) => String(item.code) === String(codeValue),
    );
    return found ? found.value : undefined;
  };

  // Maps code values to display values for dropdown fields using dropdownOptions (module scope)
  const getDisplayValue = (
    fieldName: string,
    codeValue: string | number | undefined,
    dropdownOptions: any,
  ): string => {
    // Treat special sentinel value -100 for degreeCompletionYear as the "--select--" placeholder
    if (
      fieldName === "degreeCompletionYear" &&
      (codeValue === -100 || String(codeValue) === "-100")
    ) {
      return "Non Degree Program";
    }

    if (!codeValue && codeValue !== 0) return "";

    if (fieldName === "reimbursementType") {
      const val = getDropdownValue(
        dropdownOptions.reimbursement_type,
        codeValue,
      );
      return val ?? String(codeValue);
    }

    if (fieldName === "originState" || fieldName === "destinationState") {
      const val = getDropdownValue(dropdownOptions.state, codeValue);
      return val ?? String(codeValue);
    }

    if (fieldName === "applicationTerm") {
      const val = getDropdownValue(dropdownOptions.application_term, codeValue);
      return val ?? String(codeValue);
    }

    return String(codeValue);
  };

  // Top-level function to process applications with errors (logic unchanged)

  // Fetch errors on initial load and when page changes
  React.useEffect(() => {
    dispatch(getApplicationErrors({ page: currentPage, perPage }))
      .unwrap()
      .then((data: any) => {
        const processed = processApplicationsWithErrors(data);
        setApplicationsWithErrors(processed);
        setTotalItems(data.pagination?.totalRecords || processed.length);
      });
  }, [dispatch, currentPage]);

  // Clear selected file name after successful upload and show banner
  React.useEffect(() => {
    if (uploadStatus === "succeeded") {
      setSelectedFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setShowUploadBanner(true);
    }
  }, [uploadStatus]);

  // Auto-hide upload error banner after 1 minute
  React.useEffect(() => {
    if (uploadStatus === "failed" && uploadError) {
      const timer = setTimeout(() => {
        dispatch({ type: "administer/clearUploadStatus" });
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [uploadStatus, uploadError, dispatch]);
  // Auto-hide delete-all error banner after 2 seconds
  React.useEffect(() => {
    if (deleteAllStatus === "failed" && deleteAllError) {
      const timer = setTimeout(() => {
        dispatch({ type: "administer/clearDeleteAllStatus" });
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [deleteAllStatus, deleteAllError, dispatch]);
  // Auto-clear error banners after 30 seconds
  React.useEffect(() => {
    if (fileSizeError || fileTypeError || showUploadBanner) {
      const timer = setTimeout(() => {
        setFileSizeError("");
        setFileTypeError("");
        setShowUploadBanner(false);
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [fileSizeError, fileTypeError, showUploadBanner]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteRecord = (emplApplErrId: string) => {
    dispatch(deleteOneRecord({ emplApplErrId }))
      .unwrap()
      .then(() => {
        if (applicationsWithErrors.length === 1) {
          // Last record on this page — clear locally and adjust page if needed
          setApplicationsWithErrors([]);
          setTotalItems(0);
          if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
          }
        } else {
          dispatch(getApplicationErrors({ page: currentPage, perPage }))
            .unwrap()
            .then((data: any) => {
              const processed = processApplicationsWithErrors(data);
              setApplicationsWithErrors(processed);
              setTotalItems(data.pagination?.totalRecords || processed.length);
            });
        }
      });
  };

  const handlePreviousClick = () => {
    navigate("/admin");
  };


  const handleErrorClick = (application: any) => {
    // Dynamically determine the earliest step with an error
    const earliestStep = getEarliestStep(application.errors);

    let validationErrors: Record<string, string> = {};
    let globalErrors: string[] = [];
    let errorObj: Record<string, string[]> = {};
    if (typeof application.errors === "string") {
      try {
        errorObj = JSON.parse(application.errors);
      } catch (e) {
        // fallback: treat as miscellaneous/global error
        console.error("Failed to parse application.errors:", e);
        globalErrors.push(application.errors);
      }
    } else if (
      typeof application.errors === "object" &&
      application.errors !== null
    ) {
      errorObj = application.errors;
    }
   
    if (Array.isArray(errorObj)) {
      errorObj.forEach((err: any) => {
        if (err.fieldName && err.errorMessage) {
          if (err.fieldName === "globalError" && err.backendKey) {
            const key = `globalError_${err.backendKey}`;
            validationErrors[key] = err.errorMessage;
          } else {
            validationErrors[err.fieldName] = err.errorMessage;
          }
        }
      });
    } else {
      Object.entries(errorObj).forEach(([backendKey, messages]) => {
        const frontendField = ERROR_FIELD_MAP[backendKey];
        if (frontendField === "globalError") {
          // Store each global error under a unique key
          const key = `globalError_${backendKey}`;
          validationErrors[key] = Array.isArray(messages)
            ? messages[0]
            : messages;
        } else if (frontendField) {
          validationErrors[frontendField] = Array.isArray(messages)
            ? messages[0]
            : messages;
        } else {
          globalErrors = globalErrors.concat(messages);
        }
      });
    }
   

    // Use the API response object directly for initialValues
    const initialValues = { ...application };

    // Optionally, calculate classCount if needed (fallback to 1)
    const maxClassIndex = Math.max(
      0,
      ...(application.errors || []).map((e: any) => {
        const match = /_class(\d+)/.exec(e.fieldName);
        return match ? Number(match[1]) : 0;
      }),
    );
    const classCount = maxClassIndex + 1;

    console.log(
      " Navigating to error correction for application:",
      application.applictnId || application.applicationId,
      validationErrors,
      "classCount:",
      classCount,
      "initialValues:",
      initialValues,
    );

    // Navigate to dedicated error correction route with state containing all errors, classCount, and initialValues
    navigate(`/admin/tuition/errors-to-correct`, {
      state: {
        targetStep: earliestStep, // Start at the earliest step with errors
        validationErrors: validationErrors, // Pass all field errors
        applicationId: application.applictnId || application.applicationId,
        employeeId: application.empId || application.employeeId,
        emplApplErrId: application.emplApplErrId, // Pass error record ID for error correction submit
        errors: application.errors, // Also pass full errors array
        classCount, // Pass classCount for error correction UI
        // Debug: print navigation state
        _debugNavState: {
          validationErrors,
          errorObj,
          globalErrors,
        },
        initialValues: {
          ...flattenApplicationData(application, dropdownOptions),
          reimbursementType: getDisplayValue(
            "reimbursementType",
            application.reimbType ?? application.reimbursementType,
            dropdownOptions,
          ),
          originState: getDisplayValue(
            "originState",
            application.originState,
            dropdownOptions,
          ),
          destinationState: getDisplayValue(
            "destinationState",
            application.destinationState,
            dropdownOptions,
          ),
          //  applicationTerm: getDisplayValue('applicationTerm', application.applicationTerm),
        }, // Prepopulate all other fields
      },
    });
  };

  const handleUpload = () => {
    const file = fileInputRef.current?.files?.[0] || null;
    if (!file) {
      setFileSizeError("");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileSizeError("File size exceeds limit");
      return;
    }
    setFileSizeError("");
    // Call the thunk to upload Excel file
    dispatch(uploadExcelFile({ file }));
  };

  const handleCancel = () => {
    setSelectedFileName("");
    setFileSizeError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Clear error rows only after deleteAllRecords API call succeeds
  React.useEffect(() => {
    if (deleteAllStatus === "succeeded") {
      setApplicationsWithErrors([]);
    }
  }, [deleteAllStatus]);

  return (
    <div className="lg:mx-40! mx-5! min-h-[70vh]">
      {isLoading && <Loader />}
      <BreadCrumbs
        previousPage="Workplace development"
        currentPage="Administer system"
        onPreviousClick={handlePreviousClick}
      />
      <h1 className="h1 mt-8!">Administer system</h1>
      {/* <Tabs tabs={administerSystemTabsData} /> */}
      <h3 className="mt-6!">Template to upload</h3>
      <p className="p3 -book -dolphin">
        Upload employee applications for tuition reimbursement.
      </p>

      {/* Show error banner if upload fails */}
      {uploadStatus === "failed" && uploadError && (
        <UploadErrorBanner
          header="Upload Failed"
          message={uploadError}
          className="w-[70%] mt-2!"
        />
      )}

      {showUploadBanner && (
        <div
          className="ds-banner ds-banner__bg--white ds-banner--icon !p-[24px] !mb-[24px]"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          aria-labelledby="title-4"
          style={{ borderColor: "#003B71" }}
        >
          <div className="ds-banner__icon-img">
            <img
              src="/assets/images/informationIcon.svg"
              alt=""
              style={{ width: "42px", height: "42px" }}
            />
          </div>
          <div className="ds-banner__container">
            <div className="ds-banner__main flex flex-col">
              <div
                className="ds-banner__main-header--awareness"
                id="title-4"
                style={{ fontWeight: 350, fontSize: "20px", color: "#003B71" }}
              >
                Template Uploaded
              </div>
              <div
                className="ds-banner__main-body"
                style={{ fontSize: "16px" }}
              >
                The current request is being processed. Once the scan is
                complete, you will be sent an email alert.
              </div>
            </div>
          </div>
        </div>
      )}
      {(fileSizeError || fileTypeError) && (
        <UploadErrorBanner
          header={
            fileSizeError
              ? "File size exceeds limit"
              : "File type not supported"
          }
          message={
            fileSizeError
              ? `The selected file is too large. Please upload a file that is ${MAX_FILE_SIZE_MB} MB or smaller.`
              : "Files in the upload are a format other than CSV or Excel"
          }
          className="w-[70%] mt-2!"
        />
      )}

      <div className="flex items-center gap-2 -dolphin p-0! mt-5! flex-wrap">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) {
              setSelectedFileName("");
              return;
            }
            setSelectedFileName(file.name);

            // File type validation
            const allowedTypes = [
              "text/csv",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "application/vnd.ms-excel.sheet.macroEnabled.12",
            ];
            if (allowedTypes.includes(file.type)) {
              setFileTypeError("");
            } else {
              setFileTypeError(
                "Files in the upload are a format other than CSV or Excel",
              );
              setSelectedFileName("");
              // Optionally clear file input
              if (fileInputRef.current) fileInputRef.current.value = "";
            }
          }}
          data-testid="edit-file-input"
        />

        <button
          className="button whitespace-nowrap flex items-center gap-2 shrink-0"
          onClick={() => fileInputRef.current?.click()}
        >
          <img
            src={uploadOutline}
            alt="upload icon"
            className="w-[1.6rem]! h-5! shrink-0"
            aria-hidden="true"
          />
          <span>Choose File</span>
        </button>
        {selectedFileName && (
          <span className="p3 -book ml-2" aria-live="polite">
            <span className="font-medium text-black">
              <b>{selectedFileName}</b>{" "}
            </span>
          </span>
        )}

        <ul className="p3 -book -dolphin list-disc pl-6! mt-4!">
          <li>Only CSV and Excel formats</li>
          <li>Max file size {MAX_FILE_SIZE_MB} MB</li>
        </ul>
      </div>

      {/* Buttons: Only show when a file is selected */}
      {selectedFileName && (
        <div className="flex flex-col gap-4 margin-top-2u sm:flex-row">
          <button className="button -light" onClick={handleCancel}>
            Cancel
          </button>
          <button className="button -primary -light" onClick={handleUpload}>
            Upload document
          </button>
        </div>
      )}

      {/* <div className="relative mt-6! mb-4! flex gap-2">
        <img src={InfoIcon} alt="info icon" />
        <p className="p3 -book">
          If you select cancel button you may need to re-upload documentation
        </p>
      </div> */}

      <h3 className="mt-10!">Errors to correct</h3>

      {/* Show error banner if delete all fails */}
      {deleteAllStatus === "failed" && deleteAllError && (
        <UploadErrorBanner
          header="Delete All Failed"
          message={deleteAllError}
          className="w-[70%] mt-2!"
        />
      )}
      {applicationsWithErrors.length > 0 && (
        <div className="flex items-center gap-4 mb-7! mt-6!">
          <button
            className="button  -primary"
            onClick={() => {
              dispatch(deleteAllRecords());
            }}
          >
            <img
              src={trashSolid}
              alt="Delete"
              className="w-7 h-7 inline-block text-(--Contrast-Blue,#0074AD)"
            />{" "}
            Delete all Error Uploads
          </button>
          <span className="p3 -book -dolphin">
            This will remove all rows from the error table.
          </span>
        </div>
      )}

      {applicationsWithErrors.length > 0 ? (
        <>
          <Table
            columns={[
              { key: "errors", label: "Errors" },
              { key: "applDate", label: "APPL_DATE" },
              { key: "empID", label: "EMP_ID" },
              { key: "uploadDate", label: "Upload Date" },
              { key: "fixErrors", label: "Fix Errors" },
              { key: "delete", label: "Delete" },
            ]}
            rows={applicationsWithErrors.map((app: any) => ({
              errors:
                app.errors && app.errors.length > 0 ? (
                  <ul
                    style={{
                      paddingLeft: 20,
                      margin: 0,
                      listStyleType: "disc",
                    }}
                  >
                    {app.errors.map((err: any) => (
                      <li
                        key={
                          err.fieldName +
                          "-" +
                          String(err.errorMessage).slice(0, 20)
                        }
                      >
                        {err.errorMessage}
                      </li>
                    ))}
                  </ul>
                ) : null,

              applDate: app.applDate || "",
              empID: app.empId || "",
              uploadDate: formatTimestampMDY(app.createdTime || ""),
              fixErrors: (
                <button
                  type="button"
                  style={{
                    color: "#0074AD",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => handleErrorClick(app)}
                >
                  <img
                    src={pencilIcon}
                    alt="edit"
                    className="w-4 h-4 m-1 mr-2!"
                  />{" "}
                  Edit
                </button>
              ),
              delete: (
                <button
                  type="button"
                  style={{
                    color: "#0074AD",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => handleDeleteRecord(app.emplApplErrId)}
                >
                  <img
                    src={trashSolid}
                    alt="Delete"
                    className="w-5 h-5 inline-block text-(--Contrast-Blue,#0074AD)"
                  />{" "}
                  Delete
                </button>
              ),
            }))}
          />
          <div className="mt-4 mobile:relative mobile:left-[30px]">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <p className="p3 -book -dolphin py-8">No errors found to correct.</p>
      )}
    </div>
  );
};

export default AdministerSystem;
